import React from 'react';
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  EyeIcon,
  XMarkIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

export default function PDFScannerHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-indigo-300 via-sky-100 to-white w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <DocumentTextIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">CV/Resume Scanner</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Intelligent CV scanning and candidate management system with automated interview
            scheduling, question generation, and decision tracking.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">CV Scanner & Recruitment Management Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <DocumentTextIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">CV Scanning</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Upload and scan PDF resumes with intelligent text extraction
                  and candidate information parsing for streamlined recruitment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <CalendarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Interview Scheduling</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Automated interview scheduling with calendar integration,
                  Outlook email invitations, and meeting management tools.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Question Generation</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  AI-powered interview question generation based on CV content
                  and role requirements with customizable question management.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Decision Tracking</h3>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Comprehensive candidate decision tracking with acceptance/rejection
                  workflows, notes management, and recruitment analytics.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interface Button Layouts */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interface Button Layouts</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Tab Navigation */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Main Navigation Tabs</h3>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                <div className="flex space-x-1 mb-6">
                  <button className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium opacity-75 cursor-not-allowed">
                    Scan CV
                  </button>
                  <button className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 opacity-75 cursor-not-allowed">
                    Interviews
                  </button>
                  <button className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 opacity-75 cursor-not-allowed">
                    Shortlist
                  </button>
                </div>
              </div>
            </div>

            {/* CV Scanning Interface */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">CV Scanning & Upload</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Scan CV</h2>
                </div>

                <div className="p-6">
                  <div className="space-y-6">
                    {/* File Upload */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-4">Drag and drop a PDF file here, or click to select</p>
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        disabled
                      />
                      <button className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 opacity-75 cursor-not-allowed">
                        Choose File
                      </button>
                    </div>

                    {/* Vacancy Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Vacancy
                        </label>
                        <select className="w-full border border-gray-300 rounded-lg px-3 py-2 opacity-75 cursor-not-allowed">
                          <option>Choose a vacancy...</option>
                          <option>Software Developer</option>
                          <option>Project Manager</option>
                          <option>UI/UX Designer</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          File Name
                        </label>
                        <div className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-500">
                          No file selected
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3">
                      <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 opacity-75 cursor-not-allowed">
                        Clear
                      </button>
                      <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 opacity-75 cursor-not-allowed">
                        Reject
                      </button>
                      <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 opacity-75 cursor-not-allowed">
                        Shortlist
                      </button>
                      <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 opacity-75 cursor-not-allowed">
                        Scan & Analyze
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Shortlist Management */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Shortlist Management</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Shortlisted Candidates</h2>
                </div>

                <div className="p-6">
                  <div className="space-y-6">
                    {/* Vacancy Group */}
                    <div className="border border-gray-200 rounded-lg">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-900">Software Developer (3 candidates)</h4>
                      </div>
                      
                      <div className="p-4 space-y-4">
                        {/* Candidate Card */}
                        <div className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h5 className="font-semibold text-gray-900">Jane Doe</h5>
                              <p className="text-sm text-gray-600">Uploaded: Oct 10, 2025</p>
                            </div>
                            <div className="flex space-x-2">
                              <button className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg opacity-75 cursor-not-allowed">
                                Send Assessment
                              </button>
                              <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg opacity-75 cursor-not-allowed">
                                Schedule Interview
                              </button>
                              <button className="px-3 py-1 bg-emerald-600 text-white text-sm rounded-lg opacity-75 cursor-not-allowed">
                                View CV
                              </button>
                              <button className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg opacity-75 cursor-not-allowed">
                                Reject
                              </button>
                            </div>
                          </div>

                          {/* Assessment Badge */}
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Assessment Completed
                            </span>
                            <button className="text-blue-600 hover:text-blue-800 text-sm opacity-75 cursor-not-allowed">
                              View Results
                            </button>
                          </div>

                          {/* Expanded CV View */}
                          <div className="mt-4 bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h6 className="font-semibold text-gray-900">CV Content</h6>
                              <button className="text-gray-500 hover:text-gray-700 opacity-75 cursor-not-allowed">
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                            <div className="text-sm text-gray-600 max-h-64 overflow-y-auto">
                              <p>Professional software developer with 5+ years of experience in React, Node.js, and cloud technologies...</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Assessment System */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Assessment System</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Assessment Details</h2>
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    {/* Assessment Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h5 className="font-semibold text-blue-900 mb-2">Candidate Information</h5>
                        <p className="text-sm text-blue-700">Name: John Smith</p>
                        <p className="text-sm text-blue-700">Email: john.smith@example.com</p>
                        <p className="text-sm text-blue-700">Status: Completed</p>
                      </div>

                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                        <h5 className="font-semibold text-emerald-900 mb-2">Assessment Results</h5>
                        <p className="text-sm text-emerald-700">Score: 85/100</p>
                        <p className="text-sm text-emerald-700">Completed: Oct 12, 2025</p>
                        <p className="text-sm text-emerald-700">Duration: 45 minutes</p>
                      </div>
                    </div>

                    {/* Questions & Answers */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-3">Assessment Questions</h5>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-sm font-medium text-gray-900 mb-1">1. What is your experience with React?</p>
                          <p className="text-sm text-gray-600">Answer: I have 3 years of professional experience building large-scale React applications...</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-sm font-medium text-gray-900 mb-1">2. Describe your approach to testing</p>
                          <p className="text-sm text-gray-600">Answer: I follow TDD principles and use Jest and React Testing Library...</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 opacity-75 cursor-not-allowed">
                        Close
                      </button>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 opacity-75 cursor-not-allowed">
                        Download Report
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interview Management */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Interview Management</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-green-600 to-blue-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Scheduled Interviews</h2>
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    {/* Interview Item */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">John Smith - Software Developer</h4>
                          <p className="text-sm text-gray-600">Interview scheduled for Oct 15, 2025 at 2:00 PM</p>
                        </div>
                        <div className="flex space-x-2">
                          <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg opacity-75 cursor-not-allowed">
                            Generate Questions
                          </button>
                          <button className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg opacity-75 cursor-not-allowed">
                            Accept
                          </button>
                          <button className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg opacity-75 cursor-not-allowed">
                            Reject
                          </button>
                        </div>
                      </div>

                      {/* Questions Section */}
                      <div className="mt-4">
                        <button className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 opacity-75 cursor-not-allowed">
                          <EyeIcon className="h-4 w-4" />
                          <span className="text-sm">Show Questions (3)</span>
                        </button>

                        <div className="mt-3 space-y-2">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-gray-900">Can you describe your experience with React?</p>
                              <div className="flex space-x-1">
                                <button className="text-blue-600 hover:text-blue-800 opacity-75 cursor-not-allowed">
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button className="text-red-600 hover:text-red-800 opacity-75 cursor-not-allowed">
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Schedule Interview Button */}
                    <div className="flex justify-center pt-4">
                      <button className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                        <PlusIcon className="h-5 w-5" />
                        Schedule New Interview
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interview Scheduling Form */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Interview Scheduling</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Schedule Interview</h2>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Candidate Name
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 opacity-75 cursor-not-allowed"
                        placeholder="Enter candidate name"
                        disabled
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Role
                      </label>
                      <select className="w-full border border-gray-300 rounded-lg px-3 py-2 opacity-75 cursor-not-allowed">
                        <option>Select role...</option>
                        <option>Software Developer</option>
                        <option>Project Manager</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date
                      </label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 opacity-75 cursor-not-allowed"
                        disabled
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Time
                      </label>
                      <input
                        type="time"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 opacity-75 cursor-not-allowed"
                        disabled
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 opacity-75 cursor-not-allowed"
                        placeholder="Interview location or virtual meeting link"
                        disabled
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6">
                    <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 opacity-75 cursor-not-allowed">
                      Cancel
                    </button>
                    <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 opacity-75 cursor-not-allowed">
                      Send Outlook Invite
                    </button>
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 opacity-75 cursor-not-allowed">
                      Schedule Interview
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Intelligent CV Parsing</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Advanced PDF text extraction and parsing to automatically identify
                  candidate information, skills, experience, and qualifications.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Shortlist Management</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Organize shortlisted candidates by vacancy with quick actions for
                  assessments, interviews, CV viewing, and rejection workflows.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Assessment System</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Send automated assessments to candidates and review detailed results
                  including scores, answers, and completion metrics for evaluation.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">AI Question Generation</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Smart question generation based on CV content and job requirements,
                  with customizable question banks, editing, and deletion capabilities.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Interview Lifecycle</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Complete interview management from scheduling to decision tracking,
                  with Outlook integration, question management, and accept/reject workflows.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Decision Tracking</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Structured decision-making with detailed notes, passed/failed status,
                  candidate acceptance tracking, and comprehensive recruitment analytics.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-teal-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">7</span>
                  </div>
                  <h4 className="text-lg font-bold text-teal-900">CV Expansion</h4>
                </div>
                <p className="text-teal-700 text-sm leading-relaxed">
                  View full CV text content directly in the shortlist interface with
                  expandable panels for quick candidate review without leaving the page.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-orange-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">8</span>
                  </div>
                  <h4 className="text-lg font-bold text-orange-900">Vacancy Grouping</h4>
                </div>
                <p className="text-orange-700 text-sm leading-relaxed">
                  Candidates automatically grouped by vacancy in both shortlist and
                  interview tabs with expandable sections for organized management.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-cyan-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">9</span>
                  </div>
                  <h4 className="text-lg font-bold text-cyan-900">Outlook Integration</h4>
                </div>
                <p className="text-cyan-700 text-sm leading-relaxed">
                  Generate professional Outlook calendar invitations with interview
                  details, candidate information, and automatic email sending capability.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Recruitment Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">CV Processing & Shortlisting</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Ensure CVs are in PDF format for best parsing results</li>
                    <li>• Review extracted information for accuracy before shortlisting</li>
                    <li>• Match candidates to appropriate job vacancies immediately</li>
                    <li>• Use reject button for candidates not meeting criteria</li>
                    <li>• Shortlist candidates promptly to maintain workflow momentum</li>
                    <li>• Store CVs securely with proper data protection</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">Assessment Management</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Send assessments to shortlisted candidates promptly</li>
                    <li>• Review assessment results thoroughly before interviews</li>
                    <li>• Use assessment scores as screening criteria</li>
                    <li>• Track assessment completion status regularly</li>
                    <li>• Download assessment reports for record keeping</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Interview & Question Management</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Generate interview questions using AI for consistency</li>
                    <li>• Schedule interviews from shortlist for qualified candidates</li>
                    <li>• Edit and customize generated questions as needed</li>
                    <li>• Delete irrelevant questions to maintain quality</li>
                    <li>• Group interviews by vacancy for organized review</li>
                    <li>• Use Outlook integration for professional invitations</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-pink-900 mb-3">Decision & Tracking</h3>
                  <ul className="space-y-2 text-sm text-pink-700">
                    <li>• Record detailed decision notes for all candidates</li>
                    <li>• Use passed/failed status consistently</li>
                    <li>• Track candidate acceptance/rejection responses</li>
                    <li>• Delete old interviews to keep interface clean</li>
                    <li>• Reject from shortlist when candidates don't progress</li>
                    <li>• Review recruitment metrics and process effectiveness</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}